import PropTypes from 'prop-types';
import Select from 'react-select';

/**
 * Functional version of SelectMultiField, supporting both single and multi-select modes.
 * Preserves all logic and comments from the original class component.
 */
const SelectMultiField = (props) => {
  const {
    input,
    disabled,
    label,
    labelRequired,
    placeholder,
    options,
    multi,
    meta: { touched, error, warning, submitError },
    helpText,
    cssClass,
  } = props;
  const { name, onBlur, onChange } = input;

  // Only include 'disabled' if we want that attribute to appear in the input
  // (Note: Mutating props is not recommended in React, so we pass isDisabled below instead)

  // If not multi, set value to the first item (for single-select)
  let value = input.value;
  if (!multi && Array.isArray(value)) {
    value = value[0];
  }

  // If our options aren't an array, use an empty set
  let selectOptions = [];
  if (typeof options !== 'undefined' && options !== null && typeof options.length !== 'undefined') {
    selectOptions = options;
  }

  // Map value(s) to react-select option object(s)
  const selectedOption = multi
    ? Array.isArray(value)
      ? selectOptions.filter((opt) => value.includes(opt.value))
      : []
    : selectOptions.find((opt) => opt.value === value) || null;

  // Handles value changes for both single and multi-select
  const handleChange = (selectedOptions) => {
    // Single-selects use single value
    if (!multi) {
      if (selectedOptions) {
        onChange([selectedOptions.value]);
      } else {
        onChange('');
      }
      return;
    }
    // Multi-selects use array values
    let selectedValues = [];
    if (Array.isArray(selectedOptions)) {
      selectedValues = selectedOptions.map((option) => option.value);
    }
    if (selectedValues.length === 0) {
      selectedValues = '';
    }
    onChange(selectedValues);
  };

  let finalError = error;
  if (!finalError) {
    finalError = submitError;
  }

  return (
    <div className={`form-group select-max-width select_multi${cssClass ? ' ' + cssClass : ''}${touched && error ? ' error' : ''}`}>
      {label && (
        <label htmlFor={name}>
          {label}
          {labelRequired && <abbr title="Required">*</abbr>}
        </label>
      )}
      <Select
        {...input}
        isMulti={multi}
        value={selectedOption}
        options={selectOptions}
        placeholder={placeholder}
        isDisabled={disabled}
        onBlur={() => onBlur()}
        onChange={handleChange}
        className="react-select-container"
        classNamePrefix="react-select"
      />
      {helpText && <div className="text-info">{helpText}</div>}
      {touched && finalError && <div className="text-danger">{finalError}</div>}
      {touched && warning && !finalError && <div className="text-warning">{warning}</div>}
    </div>
  );
};

SelectMultiField.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  meta: PropTypes.object,
  helpText: PropTypes.string,
  cssClass: PropTypes.string,
  multi: PropTypes.bool,
};

export default SelectMultiField;
